// App shell — route dispatcher for the OPD surfaces.
//
// Navigation model: staff routes can navigate CLIENT-SIDE (no full reload) via
// the one navTo/linkProps choke point in lib/opd-nav.js, gated by the default-
// OFF VITE_OPD_SPA_NAV / localStorage 'opd.spaNav' flag (AGENTS.md §2 ships-dark).
// With the flag OFF every nav degrades to today's full page load and this file
// is inert: installBridge already re-ran per page load, and the <Comp> key never
// changes within one document. With the flag ON, each URL change re-runs
// loadBridge (swaps the bridge CONTENTS) and ScreenBoot force-remounts the screen
// per URL, so a screen instance never survives a URL change nor renders against a
// bridge that belongs to a different URL (design §2). react-router stays imported
// ONLY here and in opd-nav.js — screens never import the router.
import React, { Suspense, lazy, useEffect, useState } from 'react';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { loadBridge } from './lib/bridge.js';
import { NotLoggedInError, classifySessionResume, activeLoginProfileId } from './lib/medplum-client.js';
import { WrongSurfaceEncounterError } from './lib/medplum-encounter.js';
import { registerNavigate, registerPrefetch } from './lib/opd-nav.js';
import { GlobalAgentButton } from './lib/GlobalAgentButton.jsx';
import { agentRouteForPath } from './lib/agent-routes.js';
import { stageableActionsForSurface, escalationsForSurface, describeSurfaceContext } from './lib/surface-actions.js';
import { effectiveRole } from './components/app/view-as.js';

// Registers react-router's navigate fn with the opd-nav choke point so navTo()
// (used by screens + the bridge click router) can do a client-side push when the
// flag is on. Renders nothing; must live INSIDE <BrowserRouter>.
function NavBridge() {
  const nav = useNavigate();
  useEffect(() => { registerNavigate(nav); }, [nav]);
  return null;
}

const Login = lazy(() => import('./screens/Login.jsx'));

// Role worlds (R1): each role logs into a closed world. owner/admin can enter
// every world (they run the hospital); everyone else only their own. A screen
// with no `roles` list admits any logged-in HUMAN staff — the bedside DEVICE
// role is contained fail-closed in ScreenBoot: it enters ONLY routes that
// explicitly list 'bedside' (the two bedside surfaces), never the roles-less
// staff screens (Settings/Tasks/Messaging/…).
const CLINICAL = ['doctor', 'owner', 'admin'];
// Intern / visiting trainees share the clinical WORK surfaces (queue, consult,
// patients, reports, schedule) so they can work up a patient in their department,
// but NOT the hospital-running surfaces (automations/admin). Their WRITE ceiling
// (no prescribe/sign) is enforced by the opd-intern-rowlevel AccessPolicy, and the
// doctor pickers/token routing exclude them via the availability role-marker
// allowlist (medplum-availability.js) — the route world is only about which screens
// they can open. (owner decision 2026-07-19)
const CLINICAL_TRAINEE = [...CLINICAL, 'intern'];
const FRONT_DESK = ['receptionist', 'reception', 'owner', 'admin'];
const ADMIN = ['owner', 'admin'];
// IPD ward world: nurses live here; doctors/admin enter for rounds & oversight.
const WARD = ['nurse', 'doctor', 'owner', 'admin'];
// The dedicated bedside-DEVICE role (opd-bedside AccessPolicy). It is admitted to
// the bedside surface ONLY — never the staff ward screens (/ward, /ward/worklist,
// /ward/bed/:id, /ward/templates all assume staff-readable data the device's RLS
// ceiling denies). So BEDSIDE = the ward staff PLUS the device, and it is applied
// to the two bedside routes alone.
const BEDSIDE = [...WARD, 'bedside'];

export const HOME_BY_ROLE = {
  owner: '/admin/settings',
  admin: '/admin/settings',
  receptionist: '/reception',
  reception: '/reception',
  nurse: '/nurse',
  doctor: '/queue',
  intern: '/queue',
  // The device lands on the bed-picker (auto-resumes to its bound bed from there).
  bedside: '/ward/bedside',
};

export function deniedRouteHome(roleValue, pathname = '') {
  const role = String(roleValue || '').toLowerCase();
  if (role === 'bedside') return '/ward/bedside';
  if (String(pathname).startsWith('/ward') && WARD.includes(role)) return '/ward';
  return HOME_BY_ROLE[role] || '/settings';
}

export function agentPatientIdFromSearch(search = '') {
  const params = new URLSearchParams(search);
  return params.get('patient') || params.get('focus') || '';
}

// Patient self-intake clinical-photo flow. ClinicalPhotosStep registers itself
// on window (side-effect import in the route's load()); this thin wrapper just
// resolves the bridge encounter id + language (same shapes ctxFromBridge uses)
// and renders it in 'self' mode, mirroring how Kiosk.jsx mounts it (mode:'self'
// here so only selfCapturable shots are offered on the patient's own phone).
function PhotosSelfFlow() {
  const encounterId = (() => {
    try {
      const b = window.OPDBridge;
      return (b && (b.intake?.encounterId
        || b.encounter?.encounter?.id
        || (b.meta && b.meta.encounterId)
        || (b.pick && b.pick('encounter.encounter.id', null)))) || '';
    } catch (e) { return ''; }
  })();
  const lang = (window.OPDBridge && window.OPDBridge.lang) || 'en';
  const Step = window.ClinicalPhotosStep;
  if (!Step || !encounterId) {
    return <BootError message="Photo capture is not available for this visit." />;
  }
  const onDone = () => {
    try {
      const meta = (window.OPDBridge && window.OPDBridge.meta) || {};
      const token = meta.token || new URLSearchParams(window.location.search).get('token') || '';
      const u = window.OPDBridge && OPDBridge.url && OPDBridge.url.intakeReview;
      window.location.href = u ? u(token) : `/intake/review${token ? `?token=${encodeURIComponent(token)}` : ''}`;
    } catch (e) { window.location.href = '/intake/review'; }
  };
  return (
    <div style={{ minHeight: '100dvh', background: 'var(--bg-canvas, #FAFBFC)', display: 'flex', flexDirection: 'column' }}>
      <Step encounterId={encounterId} lang={lang} mode="self" onDone={onDone} />
    </div>
  );
}

// route: SPA path · bootstrap: server payload name · staff: requires session
export const SCREENS = [
  { path: '/queue', bootstrap: 'queue', staff: true, roles: CLINICAL_TRAINEE, load: () => import('./screens/Queue.jsx') },
  { path: '/reception', bootstrap: 'reception', staff: true, roles: FRONT_DESK, load: () => import('./screens/Reception.jsx') },
  { path: '/nurse', bootstrap: 'queue', staff: true, roles: ['nurse', 'owner', 'admin'], load: () => import('./screens/NurseStation.jsx') },
  // IPD v2 ward world (docs/ipd/IPD_V2_DATA_CONTRACT.md §4) — Medplum-direct
  // screens; 'settings' bootstrap (currentUser + hospital), data loads in-screen.
  // Emergency (casualty / ED) — the WIRED module (docs/ipd/EMERGENCY_MODULE_DESIGN.md).
  // Reads/writes real FHIR as the logged-in user (RLS the only lock). Roles mirror
  // the ED staffing map in the design doc §9: WARD (EMO→doctor, ED nurse→nurse,
  // owner/admin) PLUS front-desk (the arrival/register desk). 'settings' bootstrap
  // like the other Medplum-direct ward screens. /emergency/:id = the per-arrival
  // clinical workspace (timeline / re-triage / vitals / notes / disposition).
  { path: '/emergency', bootstrap: 'settings', staff: true, roles: [...WARD, 'receptionist', 'reception'], load: () => import('./screens/emergency/Emergency.jsx') },
  { path: '/emergency/:id', bootstrap: 'settings', staff: true, roles: [...WARD, 'receptionist', 'reception'], load: () => import('./screens/emergency/EmergencyPatient.jsx') },
  { path: '/ward', bootstrap: 'settings', staff: true, roles: WARD, load: () => import('./screens/ward/WardBoard.jsx') },
  { path: '/ward/worklist', bootstrap: 'settings', staff: true, roles: WARD, load: () => import('./screens/ward/Worklist.jsx') },
  // Nursing charting-form editor: ward doctors use the forms in the patient
  // workspace but do not alter the hospital's nursing templates.
  { path: '/ward/templates', bootstrap: 'settings', staff: true, roles: ['nurse', 'owner', 'admin'], load: () => import('./screens/ward/TemplateEditor.jsx') },
  { path: '/ward/patients', bootstrap: 'settings', staff: true, roles: WARD, load: () => import('./screens/ward/WardPatients.jsx') },
  { path: '/ward/bed/:id', bootstrap: 'settings', staff: true, roles: WARD, load: () => import('./screens/ward/BedWorkspace.jsx') },
  // Dedicated BEDSIDE TABLET surface (patient-facing by default; staff-unlockable).
  // The device-account model (Stage 2, owner-decided 2026-07-09): the tablet lives
  // signed in as a low-privilege `bedside` USER (opd-bedside RLS); staff unlock logs
  // a real clinician in ON TOP; Lock re-logs the device back in. Admitted to the two
  // bedside routes ONLY (roles BEDSIDE = ward staff + the device) — the device must
  // never reach the staff ward screens.
  //   /ward/bedside      → the device bed-picker / setup landing (BedsidePicker)
  //   /ward/bedside/:id  → the glance + staff layers (BedsideShell)
  // NB: both match the '/ward/bed' global-agent denylist prefix below, so the
  // floating global agent stays hidden here (intended — the device never shows it).
  { path: '/ward/bedside', bootstrap: 'settings', staff: true, roles: BEDSIDE, load: () => import('./screens/ward/bedside/BedsidePicker.jsx') },
  { path: '/ward/bedside/:id', bootstrap: 'settings', staff: true, roles: BEDSIDE, load: () => import('./screens/ward/bedside/BedsideShell.jsx') },
  { path: '/patients', bootstrap: 'patients', staff: true, roles: [...CLINICAL_TRAINEE, 'receptionist', 'reception'], load: () => import('./screens/Patients.jsx') },
  { path: '/reports', bootstrap: 'reports', staff: true, roles: CLINICAL_TRAINEE, load: () => import('./screens/Reports.jsx') },
  { path: '/schedule', bootstrap: 'schedule', staff: true, roles: [...CLINICAL_TRAINEE, 'receptionist', 'reception'], load: () => import('./screens/Schedule.jsx') },
  { path: '/settings', bootstrap: 'settings', staff: true, load: () => import('./screens/Settings.jsx') },
  // Element lab — staff-only shadcn/ui base-element gallery (owner test surface).
  { path: '/lab', bootstrap: 'settings', staff: true, load: () => import('./screens/Lab.jsx') },
  { path: '/tasks', bootstrap: 'settings', staff: true, load: () => import('./screens/Tasks.jsx') },
  { path: '/messaging', bootstrap: 'settings', staff: true, load: () => import('./screens/Messaging.jsx') },
  { path: '/spaces', bootstrap: 'settings', staff: true, load: () => import('./screens/Spaces.jsx') },
  // Automation creation/editing can schedule hospital-wide messages and tasks.
  // The screen's product contract is owner/admin + doctors, not every staff role.
  { path: '/automations', bootstrap: 'settings', staff: true, roles: CLINICAL, load: () => import('./screens/Automations.jsx') },
  { path: '/admin/staff', bootstrap: 'settings', staff: true, roles: ADMIN, load: () => import('./screens/admin/Staff.jsx') },
  { path: '/admin/settings', bootstrap: 'settings', staff: true, roles: ADMIN, load: () => import('./screens/admin/Settings.jsx') },
  { path: '/admin/analytics', bootstrap: 'settings', staff: true, roles: ADMIN, load: () => import('./screens/admin/Analytics.jsx') },
  { path: '/admin/outbox', bootstrap: 'settings', staff: true, roles: ADMIN, load: () => import('./screens/admin/Outbox.jsx') },
  // Attendance is reachable by owner/admin AND by a department head (an HOD is
  // role 'doctor' with currentUser.isHOD — a flag a roles[] array can't express),
  // so the route admits 'doctor' and Attendance.jsx applies the in-screen isHOD
  // gate (a non-HOD doctor is fail-loud bounced home) + scopes the register to the
  // HOD's own department. ScreenBoot's roles[] gate is intentionally not touched.
  { path: '/admin/attendance', bootstrap: 'settings', staff: true, roles: [...ADMIN, 'doctor'], load: () => import('./screens/admin/Attendance.jsx') },
  // Static tails win over the /consult/:id wildcard in react-router ranking.
  // (/consult/:id/prescription — the high-fidelity prescription Writer mockup —
  // was RETIRED 2026-07-13: it rendered a hardcoded patient header over real
  // per-encounter meds and its sign was a no-op. The functional prescribing
  // surface is ConsultCompact below; the path now falls through to the
  // /consult/:id/* catch-all, i.e. the working consult editor.)
  { path: '/consult/:id/paper', bootstrap: 'encounter', staff: true, roles: CLINICAL_TRAINEE, load: () => import('./screens/intake/PhotoFlow.jsx') },
  // ConsultCompact is now the DEFAULT consult view (owner-approved). The old
  // three-column ConsultApp is retired from routing; /compact still resolves
  // here for any bookmarked links.
  { path: '/consult/:id/compact', bootstrap: 'encounter', staff: true, roles: CLINICAL_TRAINEE, load: () => import('./screens/consult/ConsultCompact.jsx') },
  { path: '/consult/:id/*', bootstrap: 'encounter', staff: true, roles: CLINICAL_TRAINEE, load: () => import('./screens/consult/ConsultCompact.jsx') },
  // Summary is a patient-facing magic-link surface (its own token gate) — the
  // old staff:true only forced a spurious login redirect (anon-audit finding).
  { path: '/summary', bootstrap: 'portal', staff: false, load: () => import('./screens/Summary.jsx') },
  { path: '/summary/:id', bootstrap: 'portal', staff: false, load: () => import('./screens/Summary.jsx') },
  // Staff-only kiosk device pairing. The kiosk itself (/kiosk) is a server-side
  // static page (variant chosen by hospital settings); this SPA screen just arms
  // /disarms the trusted-device cookie on the tablet it's opened on.
  { path: '/kiosk-setup', bootstrap: 'settings', staff: true, load: () => import('./screens/intake/KioskSetup.jsx') },
  // Saathi — the canonical patient-facing mobile VOICE intake: a unified multi-provider
  // widget (OpenAI / Gemini / Sarvam / ElevenLabs), custom orb + live-filling cards.
  // (The earlier /voice-intake, /realtime and /elevenlabs experiment screens were removed —
  //  their provider/agent options now live in Saathi's in-widget switcher.)
  { path: '/saathi', bootstrap: 'intake', staff: false, load: () => import('./screens/intake/MobileIntake.jsx') },
  { path: '/saathi/:id', bootstrap: 'encounter', staff: false, load: () => import('./screens/intake/MobileIntake.jsx') },
  { path: '/intake', bootstrap: 'intake', staff: false, load: () => import('./screens/intake/IntakeLanding.jsx') },
  { path: '/intake/step/:step', bootstrap: 'intake', staff: false, load: () => import('./screens/intake/IntakeSteps.jsx') },
  { path: '/intake/review', bootstrap: 'intake', staff: false, load: () => import('./screens/intake/IntakeReview.jsx') },
  // Patient self-capture of clinical photos (eyes/ears/nails/…). The doctor's
  // paper-Rx scanner (PhotoFlow) stays on /consult/:id/paper ONLY; the patient
  // route renders the shared live-camera ClinicalPhotosStep in self mode.
  { path: '/intake/photos', bootstrap: 'intake', staff: false, load: () => import('./screens/intake/ClinicalPhotosStep.jsx').then(() => ({ default: PhotosSelfFlow })) },
  { path: '/portal', bootstrap: 'portal', staff: false, load: () => import('./screens/Portal.jsx') },
  // Waiting-room display — public-safe token board. It fetches a sanitized
  // token-only payload and does not receive patient chart data.
  { path: '/token-display', bootstrap: 'token-display', staff: false, load: () => import('./screens/TokenDisplay.jsx') },
];

// Register the path→chunk-loader map so nav links can warm a route's lazy chunk
// on hover/press (opd-nav.prefetchRoute). Only STATIC screen paths are warmable
// (a param path like /consult/:id has no fixed URL to key on); the nav pills all
// point at static paths, which is exactly what benefits. Built once at module
// load — the SCREENS table never changes at runtime.
registerPrefetch(Object.fromEntries(
  SCREENS.filter((s) => !s.path.includes(':')).map((s) => [s.path, s.load]),
));

function BootSplash() {
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'var(--opd-canvas, #FAFBFC)', color: 'var(--opd-text-muted, #7A8896)',
      font: '500 14px/1.4 Inter, "Noto Sans Devanagari", system-ui, sans-serif',
    }}>
      <span className="opd-spin" style={{ width: 18, height: 18, marginRight: 10 }} />
      Loading…
    </div>
  );
}

function BootError({ message }) {
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'var(--opd-canvas, #FAFBFC)', color: 'var(--opd-danger-text, #7A1E18)',
      font: '500 14px/1.5 Inter, system-ui, sans-serif', padding: 24, textAlign: 'center',
    }}>
      <div>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>Couldn't load this screen</div>
        <div style={{ color: 'var(--opd-text-secondary, #4A5968)' }}>{message || 'The server did not respond.'}</div>
      </div>
    </div>
  );
}

// The floating admin WorldSwitcher pill was retired 2026-07-10 — world switching
// now lives in the AppShell avatar menu (components/app/nav-model.js +
// AppShell.jsx worldRows), reachable from every admin screen. See the admin
// screens' AppShell migration.

export function ScreenBoot({ screen, replaceLocation = (target) => window.location.replace(target) }) {
  const params = useParams();
  const location = useLocation();
  const urlKey = location.pathname + location.search;
  const [phase, setPhase] = useState('loading');
  const [error, setError] = useState('');
  const [redirectTo, setRedirectTo] = useState(''); // wrong-surface deep-link → SPA redirect target
  const [Comp, setComp] = useState(null);
  const [bootedUrl, setBootedUrl] = useState(''); // the urlKey the current Comp+bridge belong to
  const [agentActionContext, setAgentActionContext] = useState(null);
  const registerAgentActionContext = React.useCallback((next) => setAgentActionContext(next || null), []);
  const commandAgent = React.useMemo(
    () => ({ setActionContext: registerAgentActionContext }),
    [registerAgentActionContext],
  );

  useEffect(() => {
    let alive = true;
    setPhase('loading');
    setAgentActionContext(null);
    const token = new URLSearchParams(location.search).get('token') || '';
    const patientId = new URLSearchParams(location.search).get('patient') || '';
    const id = params.id || '';
    loadBridge(screen.bootstrap, { id, token, patientId })
      .then(async (bridge) => {
        if (!alive) return;
        if (screen.staff && !bridge.currentUser) {
          setPhase('unauth');
          return;
        }
        // Closed role worlds: a doctor never sees reception, a receptionist
        // never sees the consult room. Wrong world → bounce to your own home.
        // A full-page replace is the deliberate boundary here (rare safety path).
        // effectiveRole: an admin's "View as" lens (view-as.js) substitutes the
        // emulated role here so the lens RESTRICTS routes to that role's world.
        // It substitutes ONLY when the real role is owner/admin and every
        // emulatable role is a subset of admin's reach, so it can never ADMIT a
        // route the real role couldn't already open — pure UI restriction.
        const role = effectiveRole(String(bridge.currentUser?.role || '').toLowerCase());
        // Two containment rules, both fail-closed for non-human actors:
        //   1. a route WITH a roles list admits only those roles (unchanged);
        //   2. the bedside DEVICE enters ONLY routes that explicitly list
        //      'bedside' — a roles-less route means "any logged-in human
        //      staff", never the device (it used to slip into Settings/Tasks/
        //      Messaging/Spaces/Automations/Lab/Kiosk-setup via rule-less routes).
        const roleDenied = screen.roles && !screen.roles.includes(role);
        const bedsideDenied = role === 'bedside' && !(screen.roles || []).includes('bedside');
        if (bridge.currentUser && (roleDenied || bedsideDenied)) {
          // Bounce world-aware: a wrong-world IPD (ward) path stays in the ward
          // world (→ /ward) rather than yanking the user into their OPD home,
          // which HOME_BY_ROLE only knows. OPD paths keep their per-role home.
          // The bedside DEVICE is special-cased: it is NOT a ward-staff role, so a
          // forbidden /ward/* path must send it to its OWN home (/ward/bedside),
          // NOT to /ward (also forbidden for it → would loop).
          const home = deniedRouteHome(role, location.pathname);
          replaceLocation(home);
          return;
        }
        // Import the screen module only after the bridge is live — ported
        // screens read window.OPDBridge at mount.
        const mod = await screen.load();
        if (!alive) return;
        setComp(() => mod.default);
        setBootedUrl(urlKey); // stamp which URL this boot serves (the remount guard)
        setPhase('ready');
      })
      .catch((e) => {
        if (!alive) return;
        // Wrong-surface deep link: a /consult/:id whose encounter is inpatient
        // (IMP) or emergency (EMER) — the consult loader throws this typed error
        // carrying the encounter's REAL home (/ward/bed/:id or /emergency/:id).
        // SPA-redirect there with <Navigate replace> — no reload, and NOTHING
        // touches the Medplum session (a wrong surface must never log anyone out).
        // Checked before NotLoggedInError so it can never be mistaken for auth.
        if (e instanceof WrongSurfaceEncounterError) {
          setRedirectTo(e.target);
          setPhase('redirect');
          return;
        }
        // The REAL session is the auth gate: a converted loader with no live
        // Medplum session (never logged in, or the refresh token expired and a
        // read 401'd) throws NotLoggedInError → route to /login, preserving
        // where the user was headed (state.from below). No retry loops.
        //
        // OFFLINE ≠ EXPIRED: but a NotLoggedInError raised while we are OFFLINE
        // with a login still on disk is NOT auth death — it is an unverifiable
        // session on a tablet that dropped wifi. Bouncing to /login there would
        // strand the user (they can't reach /login either) and destroy a session
        // that is fine. getMedplum() already suppresses this case, so this is
        // defense-in-depth for any other loader that throws NotLoggedInError; we
        // fall through to the honest boot-error state instead of /login.
        if (e instanceof NotLoggedInError && screen.staff) {
          const offlineWithSession = classifySessionResume({
            networkFailure: typeof navigator !== 'undefined' && navigator.onLine === false,
            hasStoredLogin: !!activeLoginProfileId(),
          }) === 'offline';
          if (!offlineWithSession) {
            setPhase('unauth');
            return;
          }
        }
        setError(e.message);
        setPhase('error');
      });
    return () => { alive = false; };
    // Under full reload this effect runs once per page load; under SPA nav it
    // runs on every URL change (same dep set, folded to urlKey).
  }, [urlKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (phase === 'unauth') return <Navigate to="/login" replace state={{ from: urlKey }} />;
  if (phase === 'redirect') return <Navigate to={redirectTo} replace />;
  if (phase === 'error') return <BootError message={error} />;
  // The remount guard (design §2): ScreenBoot is the same component type at the
  // same tree position for every route, so React preserves its instance across a
  // URL change. Effects run AFTER render, so without this a committed frame would
  // render the new URL against the OLD bridge (for /consult/A → /consult/B that
  // frame is patient A's data under patient B's URL). Hold the splash until the
  // boot for THIS urlKey has completed.
  if (phase !== 'ready' || !Comp || bootedUrl !== urlKey) return <BootSplash />;
  const path = location.pathname;
  const user = (window.OPDBridge && window.OPDBridge.currentUser) || null;
  const agentRoute = agentRouteForPath(path, user);
  const showGlobalAgent = screen.staff && (agentRoute.owner === 'dock' || agentRoute.owner === 'floating');
  const agentContext = agentRoute.context;
  const surfaceActions = agentContext ? stageableActionsForSurface(agentContext.surface, user) : [];
  const escalations = agentContext ? escalationsForSurface(agentContext.surface, user) : [];
  const surfaceContext = agentContext ? describeSurfaceContext(agentContext.surface, agentActionContext) : null;
  // Keep the floating instance mounted while CSS swaps ownership at the route's
  // actual rail breakpoint. Its conversation therefore survives resize, and the
  // dock/FAB bands cannot overlap or leave a gap.
  const globalVisibilityClass = agentRoute.owner === 'dock'
    ? (agentContext.alwaysDock ? 'hidden'
      : agentContext.breakpoint === 1281 ? 'min-[1281px]:hidden'
      : agentContext.breakpoint === 1280 ? 'min-[1280px]:hidden'
        : agentContext.breakpoint === 1181 ? 'min-[1181px]:hidden' : 'lg:hidden')
    : '';
  const patientId = agentPatientIdFromSearch(location.search);
  return (
    <>
      {/* route-enter: a subtle, fast content-enter (opacity + 4px, ~170ms
          ease-out, reduced-motion aware) so a freshly-arrived screen settles in
          instead of snapping. Keyed by bootedUrl so it replays on each URL
          change. Saathi is itself a fixed, full-viewport app, so its wrapper uses
          opacity only: an animated transform would become its containing block
          and collapse the fixed intake to the wrapper's zero content height.
          The floating agent (below) is a sibling and is intentionally NOT
          wrapped — it must not fade on every navigation. */}
      <div key={bootedUrl} className={`route-enter${path.startsWith('/saathi') ? ' route-enter-fixed' : ''}`}>
        <Comp commandAgent={commandAgent} /> {/* forced remount per URL, incl. same-route param changes */}
      </div>
      {/* World switching now lives in the AppShell avatar menu (nav-model);
          the floating WorldSwitcher pill was retired 2026-07-10. */}
      {showGlobalAgent && (
        <GlobalAgentButton
          key={`ga:${bootedUrl}`}
          className={globalVisibilityClass}
          pageContext={{
            ...agentContext,
            patientId: agentActionContext?.patientId || patientId || undefined,
            patientName: agentActionContext?.patientName || undefined,
            encounterId: agentActionContext?.encounterId || undefined,
          }}
          surfaceActions={surfaceActions}
          escalations={escalations}
          surfaceContext={surfaceContext}
          actionContext={agentActionContext}
        />
      )}
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <NavBridge />
      <Suspense fallback={<BootSplash />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          {SCREENS.map((s) => (
            <Route key={s.path} path={s.path} element={<ScreenBoot screen={s} />} />
          ))}
          <Route path="/" element={<Navigate to="/queue" replace />} />
          <Route path="*" element={<Navigate to="/queue" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
